import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { loadNoteWindows, saveNoteWindows } from '@/lib/noteWindows/storage';
import { MAX_WINDOWS } from '@/lib/noteWindows/types';
import type { NoteWindow, NoteWindowMeta } from '@/lib/noteWindows/types';

/**
 * Desktop-style note windows: a note opens as a window, clicking outside
 * minimizes it instead of closing it, and every note you opened stays in the
 * dock until you close it. See docs/MULTI_APP_WORKSPACE.md §5.
 *
 * This provider lives above <Routes>, so the windows survive every in-app
 * navigation by construction; localStorage carries them across reloads.
 */

interface State {
  windows: NoteWindow[];
  maxZ: number;
  /** Which user's storage this state was hydrated from — guards the save. */
  hydratedFor: number | null;
}

type Action =
  | { type: 'hydrate'; userId: number | null; windows: NoteWindow[]; maxZ: number }
  | { type: 'open'; noteId: number; meta: NoteWindowMeta }
  | { type: 'minimize'; noteId: number }
  | { type: 'close'; noteId: number }
  | { type: 'closeAll' }
  | { type: 'toggleMaximize'; noteId: number }
  | { type: 'meta'; noteId: number; meta: NoteWindowMeta };

const INITIAL: State = { windows: [], maxZ: 0, hydratedFor: null };

function mergeMeta(win: NoteWindow, meta: NoteWindowMeta): NoteWindow {
  const locked = meta.locked ?? win.locked;
  return {
    ...win,
    title: meta.title !== undefined ? meta.title : win.title,
    color: meta.color !== undefined ? meta.color : win.color,
    locked,
    // A locked note's content never gets cached, not even in memory.
    snippet: locked ? null : meta.snippet !== undefined ? meta.snippet : win.snippet,
  };
}

/** Keeps the dock bounded, dropping the stalest minimized windows first. */
function evict(windows: NoteWindow[]): NoteWindow[] {
  if (windows.length <= MAX_WINDOWS) return windows;

  const removable = windows
    .filter((win) => win.state === 'minimized')
    .sort((a, b) => a.lastFocusedAt - b.lastFocusedAt)
    .slice(0, windows.length - MAX_WINDOWS);

  return windows.filter((win) => !removable.includes(win));
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'hydrate':
      return {
        windows: action.windows,
        maxZ: action.maxZ,
        hydratedFor: action.userId,
      };

    case 'open': {
      const now = Date.now();
      const z = state.maxZ + 1;
      const existing = state.windows.find((win) => win.noteId === action.noteId);

      // Decided behaviour: one note visible at a time — opening another one
      // sends the current one to the dock rather than closing it. §5.1 rule 3
      const others = state.windows
        .filter((win) => win.noteId !== action.noteId)
        .map((win) => (win.state === 'open' ? { ...win, state: 'minimized' as const } : win));

      const opened: NoteWindow = existing
        ? { ...mergeMeta(existing, action.meta), state: 'open', z, lastFocusedAt: now }
        : mergeMeta(
            {
              noteId: action.noteId,
              title: null,
              snippet: null,
              color: null,
              locked: false,
              state: 'open',
              mode: 'floating',
              z,
              openedAt: now,
              lastFocusedAt: now,
            },
            action.meta,
          );

      return { ...state, windows: evict([...others, opened]), maxZ: z };
    }

    case 'minimize':
      return {
        ...state,
        windows: state.windows.map((win) =>
          win.noteId === action.noteId ? { ...win, state: 'minimized' } : win,
        ),
      };

    case 'close':
      return { ...state, windows: state.windows.filter((win) => win.noteId !== action.noteId) };

    case 'closeAll':
      return { ...state, windows: [] };

    case 'toggleMaximize':
      return {
        ...state,
        windows: state.windows.map((win) =>
          win.noteId === action.noteId
            ? { ...win, mode: win.mode === 'maximized' ? 'floating' : 'maximized' }
            : win,
        ),
      };

    case 'meta':
      return {
        ...state,
        windows: state.windows.map((win) =>
          win.noteId === action.noteId ? mergeMeta(win, action.meta) : win,
        ),
      };

    default:
      return state;
  }
}

export interface NoteWindowsApi {
  windows: NoteWindow[];
  /** The single window currently on screen, if any. */
  openWindow: NoteWindow | null;
  /** Opens, restores or focuses the window for a note. */
  open: (noteId: number, meta?: NoteWindowMeta) => void;
  minimize: (noteId: number) => void;
  close: (noteId: number) => void;
  closeAll: () => void;
  toggleMaximize: (noteId: number) => void;
  /** Keeps the dock label in step with what the user is typing. */
  setMeta: (noteId: number, meta: NoteWindowMeta) => void;
  has: (noteId: number) => boolean;
}

const NoteWindowsContext = createContext<NoteWindowsApi | null>(null);

export function NoteWindowsProvider({ children }: { children: ReactNode }) {
  const { user, currentUser } = useAuth();
  const userId = currentUser?.id ?? user?.id ?? null;
  const [state, dispatch] = useReducer(reducer, INITIAL);

  // Hydrate on boot and whenever the signed-in user changes.
  useEffect(() => {
    if (userId == null) {
      dispatch({ type: 'hydrate', userId: null, windows: [], maxZ: 0 });
      return;
    }
    const { windows, maxZ } = loadNoteWindows(userId);
    dispatch({ type: 'hydrate', userId, windows, maxZ });
  }, [userId]);

  // Persist, debounced. Skipped until hydration for this user has landed, so
  // the empty initial state never overwrites what is stored.
  useEffect(() => {
    if (userId == null || state.hydratedFor !== userId) return;
    const timer = setTimeout(() => saveNoteWindows(userId, state.windows, state.maxZ), 200);
    return () => clearTimeout(timer);
  }, [userId, state.hydratedFor, state.windows, state.maxZ]);

  const open = useCallback(
    (noteId: number, meta: NoteWindowMeta = {}) => dispatch({ type: 'open', noteId, meta }),
    [],
  );
  const minimize = useCallback((noteId: number) => dispatch({ type: 'minimize', noteId }), []);
  const close = useCallback((noteId: number) => dispatch({ type: 'close', noteId }), []);
  const closeAll = useCallback(() => dispatch({ type: 'closeAll' }), []);
  const toggleMaximize = useCallback(
    (noteId: number) => dispatch({ type: 'toggleMaximize', noteId }),
    [],
  );
  const setMeta = useCallback(
    (noteId: number, meta: NoteWindowMeta) => dispatch({ type: 'meta', noteId, meta }),
    [],
  );

  const value = useMemo<NoteWindowsApi>(
    () => ({
      windows: state.windows,
      openWindow: state.windows.find((win) => win.state === 'open') ?? null,
      open,
      minimize,
      close,
      closeAll,
      toggleMaximize,
      setMeta,
      has: (noteId: number) => state.windows.some((win) => win.noteId === noteId),
    }),
    [state.windows, open, minimize, close, closeAll, toggleMaximize, setMeta],
  );

  return <NoteWindowsContext.Provider value={value}>{children}</NoteWindowsContext.Provider>;
}

export function useNoteWindows(): NoteWindowsApi {
  const ctx = useContext(NoteWindowsContext);
  if (!ctx) throw new Error('useNoteWindows must be used inside <NoteWindowsProvider>');
  return ctx;
}
